import 'next-auth/jwt'
import NextAuth, { Session } from 'next-auth'
import Credentials from 'next-auth/providers/credentials'
import { isResponseError, up } from 'up-fetch'
import { z } from 'zod'

import {
  GetAddressSchema,
  GetCompanyAccountSchema,
  GetCompanyProfileSchema,
  GetCompanySchema,
  GetUserSchema,
} from './zod'

const UserSchema = GetUserSchema.extend({
  company: GetCompanySchema.extend({
    account: GetCompanyAccountSchema,
    address: GetAddressSchema,
    profile: GetCompanyProfileSchema,
  }).nullish(),
})
type UserType = z.infer<typeof UserSchema>

const UserContactsSchema = z.object({
  data: z
    .object({
      accessibilities: z.object({
        method: z.string(),
        times: z
          .object({
            day: z.number(),
            time: z.string(),
          })
          .array(),
      }),
      email: z.string(),
      hx: z.string(),
      id: z.number(),
      member_id: z.number(),
      method: z.string(),
      name: z.string(),
      nickname: z.string(),
      notes: z.string(),
      phone: z.string(),
      position: z.object({
        id: z.number(),
        title: z.string(),
      }),
      style: z.string(),
      timocom_id: z.string(),
      trans_id: z.string(),
      type: z.string().nullish(),
      whatsapp: z.string(),
    })
    .array(),
})
type UserContactsData = z.infer<typeof UserContactsSchema>

declare module 'next-auth' {
  interface Session {
    error?: 'AccessTokenError'
    user?: Omit<UserType, 'id'> & {
      access_token?: string
      id?: string
    }
    userContacts?: UserContactsData
  }

  interface User {
    access_token?: string
    expired_at: number
  }
}

declare module 'next-auth/jwt' {
  interface JWT {
    access_token?: string
    error?: 'AccessTokenError'
    expired_at: number
  }
}

const getUpFetch = ({ access_token }: { access_token?: string }) =>
  up(fetch, () => ({
    baseUrl: process.env.API_URL,
    headers: {
      Accept: 'application/json',
      'App-Source': process.env.SOURCE,
      Authorization: access_token ? `Bearer ${access_token}` : undefined,
      'Content-Type': 'application/json',
    },
    onError: error => {
      console.error(error)
    },
  }))

export const { auth, handlers, signIn, signOut } = NextAuth({
  callbacks: {
    async jwt({ token, user }) {
      if (user) {
        return {
          ...token,
          ...user,
        }
      } else if (Date.now() < token.expired_at * 1000) {
        return token
      } else {
        if (!token.access_token) throw new Error('Missing access_token')

        try {
          const upFetch = getUpFetch({ access_token: token.access_token })

          const res = await upFetch<{
            access_token: string
            expires_in: number
          }>('/auth/refresh', {
            method: 'POST',
          })

          const { access_token, expires_in } = res

          return {
            ...token,
            access_token,
            expires_at: Math.floor(Date.now() / 1000 + expires_in),
          }
        } catch (e) {
          if (isResponseError(e)) {
            console.error('Token refresh error:', e.data.message)
          }

          token.error = 'AccessTokenError'
          return token
        }
      }
    },
    async session({ session, token }) {
      session.error = token.error
      session.user.access_token = token.access_token

      const upFetch = getUpFetch({ access_token: token.access_token })

      try {
        const res = await upFetch<{ data: UserType }>('/users/me', {
          params: {
            i: 'company.account|company.country|company.profile.status|company.profile.type',
          },
        })

        if (res.data) {
          session.user = {
            ...session.user,
            ...res.data,
            id: `${res.data.id}`,
          } satisfies Session['user']
        }

        if (res.data?.company?.id) {
          session.userContacts = await upFetch<UserContactsData>(
            `/companies/${res.data.company.id}/contacts`,
            {
              params: {
                'filter[type]': 'transport,accounts',
              },
            },
          )
        }

        return session
      } catch (e) {
        if (isResponseError(e)) {
          console.error('Session error:', e.data.message)
        }

        return {
          ...session,
          error: 'AccessTokenError',
          user: undefined,
        }
      }
    },
  },
  cookies: {
    callbackUrl: {
      name: process.env.CALLBACK_URL_COOKIE_NAME,
    },
    csrfToken: {
      name: process.env.CSRF_TOKEN_COOKIE_NAME,
    },
    sessionToken: {
      name: process.env.SESSION_TOKEN_COOKIE_NAME,
    },
  },
  providers: [
    Credentials({
      authorize: async credentials => {
        const res = await fetch(`${process.env.API_URL}/users/me/`, {
          headers: {
            'App-Source': process.env.NEXT_PUBLIC_SOURCE ?? '',
            Authorization: `Bearer ${credentials.accessToken}`,
          },
        })

        if (!res.ok) {
          return null
        }

        const user: {
          data: {
            email: string
            id: number
            name: string
          }
        } = await res.json()

        console.info(user)

        return {
          access_token: `${credentials.accessToken}`,
          expired_at: Math.floor(Date.now() / 1000 + parseInt(`${credentials.exp}`)),
          id: `${user.data.id}`,
        }
      },
      credentials: {
        accessToken: {},
        exp: {},
      },
    }),
  ],
  session: {
    maxAge: 60 * 60 * 7,
  },
})

export type User = Session['user']
export type { Session } from 'next-auth'
export { SessionProvider, useSession } from 'next-auth/react'
